import React from 'react';
import { useGoogleStats } from '../hooks/useGoogleStats';

const StarRating = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill={i < Math.round(rating) ? '#FBBF24' : 'none'}
        stroke={i < Math.round(rating) ? '#F59E0B' : '#D1D5DB'}
        strokeWidth="1.5"
        className="flex-shrink-0"
      >
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    ))}
  </div>
);

const GoogleReviewsBadge = () => {
  // Shared with the "Recent installs" Google summary card via the
  // useGoogleStats module-level cache — one fetch per page load.
  const { stats } = useGoogleStats();

  // Keep the existing behavior: badge stays hidden until live data arrives
  // (the floating widget shouldn't pop in with placeholder numbers).
  if (!stats?.totalReviews || !stats?.overallRating) return null;

  return (
    <a
      href="/reviews"
      className="fixed bottom-6 left-4 md:bottom-6 md:left-4 z-40 group max-sm:bottom-24 max-sm:left-auto max-sm:right-3"
      aria-label={`Google Reviews: ${stats?.overallRating} stars from ${stats?.totalReviews} reviews`}
    >
      {/* Glow ring */}
      <span className="absolute inset-0 rounded-2xl bg-blue-400 opacity-20 blur-md group-hover:opacity-40 transition-opacity duration-300 pointer-events-none" />

      {/* Desktop layout */}
      <div className="relative hidden sm:flex items-center gap-3 bg-white rounded-2xl px-4 py-3 border border-gray-200 shadow-[0_4px_20px_rgba(0,0,0,0.15)] group-hover:shadow-[0_8px_30px_rgba(0,0,0,0.2)] group-hover:-translate-y-1 transition-all duration-200">
        <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-white border border-gray-100 shadow-sm flex items-center justify-center">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.33 0 9.25-3.65 9.25-9.09 0-1.15-.15-1.81-.15-1.81z" fill="#4285F4"/>
          </svg>
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Google Reviews</span>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-extrabold text-gray-900 leading-none">{stats?.overallRating}</span>
            <StarRating rating={stats?.overallRating} />
          </div>
          <span className="text-xs text-gray-500 mt-0.5">
            {stats?.totalReviews?.toLocaleString()} verified reviews
          </span>
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 group-hover:stroke-blue-500 group-hover:translate-x-0.5 transition-all duration-200">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>

      {/* Mobile compact layout */}
      <div className="relative flex sm:hidden items-center gap-1.5 bg-white rounded-xl px-2.5 py-1.5 border border-gray-200 shadow-[0_2px_12px_rgba(0,0,0,0.12)] transition-all duration-200">
        <div className="flex-shrink-0 w-6 h-6 rounded-lg bg-white border border-gray-100 shadow-sm flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.33 0 9.25-3.65 9.25-9.09 0-1.15-.15-1.81-.15-1.81z" fill="#4285F4"/>
          </svg>
        </div>
        <div className="flex flex-col leading-tight">
          <div className="flex items-center gap-1">
            <span className="text-xs font-extrabold text-gray-900 leading-none">{stats?.overallRating}</span>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }, (_, i) => (
                <svg key={i} width="9" height="9" viewBox="0 0 24 24"
                  fill={i < Math.round(stats?.overallRating) ? '#FBBF24' : 'none'}
                  stroke={i < Math.round(stats?.overallRating) ? '#F59E0B' : '#D1D5DB'}
                  strokeWidth="1.5" className="flex-shrink-0">
                  <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                </svg>
              ))}
            </div>
          </div>
          <span className="text-[9px] text-gray-400 leading-tight">{stats?.totalReviews} reviews</span>
        </div>
      </div>
    </a>
  );
};

export default GoogleReviewsBadge;
